import { IMapInstance, IMapMarker, IMapProvider } from "../../../../services/maps/map-provider.interface";
import { IStoreLocation } from "../../../../models/data.interface";
import { MapMarkerInfoWindow } from "../../../shared/MapMarkerInfoWindow/map-marker-info-window";
import { LocalizationService } from "../../../../services/localization-service";
import { Data } from "../../../../models/data";
import { Context } from "../../../../models/context";

export class StoreLocatorMap {
    private readonly _mapProvider: IMapProvider;
    private _mapInstance: IMapInstance | null = null;
    private _markers: { marker: IMapMarker; storeLocation: IStoreLocation }[] = [];
    private readonly _defaultMapZoom = 10;
    private readonly _mapMarkerInfoWindow: MapMarkerInfoWindow;
    private readonly _data: Data;
    private readonly _localizationService: LocalizationService;
    private readonly _context: Context;
    public _onStoreSelected: ((storeKey: string) => void) | null = null;
    public _onMarkerClicked: ((storeKey: string) => void) | null = null;
    public _onMapIdle: (() => void) | null = null;
    private _visibleStoreCount: number = 0;

    constructor(
        mapProvider: IMapProvider,
        data: Data,
        localizationService: LocalizationService,
        context: Context,
    ) {
        this._mapProvider = mapProvider;
        this._data = data;
        this._localizationService = localizationService;
        this._context = context;
        this._mapMarkerInfoWindow = new MapMarkerInfoWindow(data, localizationService);
    }

    get mapInstance(): IMapInstance | null {
        return this._mapInstance;
    }


    public async loadMap(
        mapContainer: HTMLElement,
        mapId: string,
        center: { lat: number; lng: number },
        initialZoom: number,
    ): Promise<void> {
        this._mapInstance = await this._mapProvider?.createMap(mapContainer, {
            zoom: initialZoom,
            center: center,
            disableDefaultUI: true,
            gestureHandling: "greedy",
            mapId: mapId,
        });

        // Add listener to update visible locations when map becomes idle
        this._mapInstance?.onIdle(() => this._onMapIdle?.());
    }

    public async createMarkers(storeLocations: IStoreLocation[], iconUrl: string): Promise<void> {
        if (!this._mapInstance) return;

        // Clear existing markers
        this._mapInstance.clearMarkers();
        this._markers = [];

        // Add new markers
        for (const storeLocation of storeLocations) {
            const marker = await this._mapInstance?.addMarkerAsync(
                { lat: storeLocation.latitude, lng: storeLocation.longitude },
                { iconUrl: iconUrl },
            );

            if (marker) {
                const markerInfo = { marker, storeLocation };

                marker?.onClick(() => {
                    this._showStoreInfo(markerInfo);
                    this._onMarkerClicked?.(markerInfo.storeLocation.storeKey);
                });
                this._markers.push(markerInfo);
            }
        }

        this._mapInstance.setClusterMarkers?.(this._markers.map((m) => m.marker));
    }

    private _showStoreInfo(markerInfo: { marker: IMapMarker; storeLocation: IStoreLocation }): void {
        const languageCode = this._context.getProperty("languageCode");
        const infoHtml = this._mapMarkerInfoWindow.createStoreInfoWindow(
            markerInfo.storeLocation,
            languageCode,
            () => this._onStoreSelected?.(markerInfo.storeLocation.storeKey),
            "ChooseStore"
        );
        this._mapInstance?.openInfo(markerInfo.marker, infoHtml);
    }

    public _showStoreOnMap(storeKey: string): void {
        const markerInfo = this._markers.find((m) => m.storeLocation.storeKey === storeKey);

        if (markerInfo) {
            this.centerMapOnCoordinates(markerInfo.marker.getPosition(), this._defaultMapZoom);
            this._showStoreInfo(markerInfo);
        }
    }

    public getVisibleStoreKeys(): string[] {
        if (!this._mapInstance) return [];

        const visibleStoreKeys = this._markers
            .filter((markerInfo) => this._mapInstance?.isInView(markerInfo.marker.getPosition()))
            .map((markerInfo) => markerInfo.storeLocation.storeKey);

        this._visibleStoreCount = visibleStoreKeys.length;
        return visibleStoreKeys;
    }

    public adjustZoomIfNoStoresVisible(): void {
        if (this._visibleStoreCount > 0) return;

        const currentZoom = this._mapInstance?.getZoom() ?? 2;
        const minZoomLevel = 4;

        if (currentZoom > minZoomLevel) this._mapInstance?.setZoom(currentZoom - 2);
    }

    public centerMapOnCoordinates(coords: { lat: number; lng: number }, zoom?: number): void {
        this._mapInstance?.setCenter(coords);
        if (zoom !== undefined) {
            this._mapInstance?.setZoom(zoom);
        }
    }
}
